/**
 * InterDepartmentRequest — внутренние заявки между отделами отеля.
 *
 * Тонкая MobX-обёртка над /api/v1/admin/interdept-requests. Используется:
 *   - кнопкой «Заявка на ресепшен» в FitnessAdminPage
 *   - страницей /requests (inbox ресепшена)
 *   - бейджем в сайдбаре (только счётчик)
 *
 * Список делим на «inbox» (на мой отдел) / «sent» (от меня) / «all».
 * По умолчанию фильтр статуса — только открытые (new + in_progress).
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { message } from 'antd';
import { apiClient, envUrls } from '../../shared/api/api-client';

export type Department =
  | 'reception'
  | 'fitness'
  | 'housekeeping'
  | 'laundry'
  | 'maintenance'
  | 'restaurant'
  | 'concierge'
  | 'other';

export type RequestType = 'supply' | 'pickup' | 'repair' | 'task' | 'other';

export type RequestStatus = 'new' | 'in_progress' | 'done' | 'cancelled';

export type RequestPriority = 'normal' | 'high' | 'urgent';

export interface InterDeptRequest {
  id: number;
  platform_id: number;
  from_department: Department;
  from_department_display: string;
  to_department: Department;
  to_department_display: string;
  /** Группа доступа, на которую поставлена задача. Для задач,
   *  созданных до перехода на группы, пусто. */
  to_group_id: number | null;
  to_group_name: string;
  request_type: RequestType;
  request_type_display: string;
  title: string;
  quantity: number | null;
  comment: string;
  status: RequestStatus;
  status_display: string;
  created_by_id: number | null;
  created_by_name: string;
  /** Кто взял задачу в работу. Из числа назначенных. */
  assignee_id: number | null;
  assignee_name: string;
  /** Кому поручено. Исполнители равны: взять и завершить может любой. */
  assignees: { id: number; name: string }[];
  /** Может ли текущий пользователь править задачу. Считает бэкенд:
   *  автор или управляющий, и только пока задача не закрыта. */
  can_edit: boolean;
  /* Treema-поля (top-level «Задачи») */
  location: string;
  due_at: string | null;
  priority: RequestPriority;
  photos: string[];
  watchers: { id: number; name: string }[];
  accepted_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRequestPayload {
  platform_id: number;
  to_department?: Department;
  to_group_id?: number | null;
  from_department?: Department;
  request_type?: RequestType;
  title: string;
  quantity?: number | null;
  comment?: string;
  location?: string;
  due_at?: string | null;
  priority?: RequestPriority;
  photos?: string[];
  /** Одного исполнителя шлёт мобильное приложение; из кабинета идёт
   *  assignee_ids — задачу часто делают вдвоём. */
  assignee_id?: number | null;
  assignee_ids?: number[];
  watcher_ids?: number[];
}

export type Box = 'inbox' | 'sent' | 'all';

export class InterDeptRequestsStore {
  items: InterDeptRequest[] = [];
  loading = false;
  error: string | null = null;

  box: Box = 'inbox';
  /** Статусы через запятую. Пусто = только открытые на бэке. */
  statusFilter = '';
  /** Поиск по названию/месту/комментарию (Treema-список). */
  search = '';
  /** Фильтр по приоритету ('' = все). */
  priorityFilter = '';
  /** Фильтр по отделу-исполнителю ('' = все). Остался для старых
   *  задач: отели работают группами доступа, см. toGroupFilter. */
  toDepartmentFilter = '';
  /** Фильтр по группе доступа ('' = все). */
  toGroupFilter = '';

  constructor() {
    makeAutoObservable(this);
  }

  setBox(box: Box) {
    this.box = box;
  }

  setStatusFilter(s: string) {
    this.statusFilter = s;
  }

  setSearch(s: string) {
    this.search = s;
  }

  setPriorityFilter(p: string) {
    this.priorityFilter = p;
  }

  setToDepartmentFilter(d: string) {
    this.toDepartmentFilter = d;
  }

  setToGroupFilter(id: string) {
    this.toGroupFilter = id;
  }

  async fetch(platformId: number) {
    this.loading = true;
    this.error = null;
    try {
      const params: Record<string, string | number> = {
        platform_id: platformId,
        box: this.box,
      };
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.search) params.search = this.search;
      if (this.priorityFilter) params.priority = this.priorityFilter;
      if (this.toDepartmentFilter) params.to_department = this.toDepartmentFilter;
      if (this.toGroupFilter) params.to_group_id = this.toGroupFilter;
      const r = await apiClient.get(
        `${envUrls.api}/api/v1/admin/interdept-requests`,
        { params },
      );
      runInAction(() => {
        this.items = r.data?.items ?? [];
      });
    } catch (e: any) {
      runInAction(() => {
        this.error =
          e?.response?.data?.error || e?.message || 'Ошибка загрузки заявок';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async create(payload: CreateRequestPayload): Promise<InterDeptRequest | null> {
    try {
      const r = await apiClient.post(
        `${envUrls.api}/api/v1/admin/interdept-requests`,
        payload,
      );
      message.success('Заявка отправлена');
      const created: InterDeptRequest = r.data;
      runInAction(() => {
        this.items = [created, ...this.items];
      });
      return created;
    } catch (e: any) {
      const msg =
        e?.response?.data?.error || e?.message || 'Не удалось создать заявку';
      message.error(msg);
      return null;
    }
  }

  async patch(
    id: number,
    payload: Partial<
      Pick<
        InterDeptRequest,
        | 'status'
        | 'comment'
        | 'assignee_id'
        | 'location'
        | 'due_at'
        | 'priority'
        | 'photos'
        | 'title'
        | 'to_department'
      >
    > & {
      watcher_ids?: number[];
      assignee_ids?: number[];
      to_group_id?: number | null;
    },
    platformId?: number,
  ): Promise<InterDeptRequest | null> {
    try {
      const params: Record<string, string | number> = {};
      if (platformId) params.platform_id = platformId;
      const r = await apiClient.patch(
        `${envUrls.api}/api/v1/admin/interdept-requests/${id}`,
        payload,
        { params },
      );
      const updated: InterDeptRequest = r.data;
      runInAction(() => {
        this.items = this.items.map((x) => (x.id === id ? updated : x));
      });
      return updated;
    } catch (e: any) {
      const msg =
        e?.response?.data?.error || e?.message || 'Не удалось обновить заявку';
      message.error(msg);
      return null;
    }
  }
}

/** Лёгкий счётчик — для бейджа в сайдбаре, без загрузки полного списка. */
export async function fetchInterDeptInboxCount(
  platformId: number,
  toDepartment?: Department,
): Promise<number> {
  try {
    const params: Record<string, string | number> = { platform_id: platformId };
    if (toDepartment) params.to_department = toDepartment;
    const r = await apiClient.get(
      `${envUrls.api}/api/v1/admin/interdept-requests/inbox-count`,
      { params },
    );
    return Number(r.data?.count || 0);
  } catch {
    return 0;
  }
}

/** Пресеты быстрых заявок — стандартные шаблоны для отделов. */
export interface RequestPreset {
  key: string;
  title: string;
  request_type: RequestType;
  to_department: Department;
  defaultQty?: number;
  icon?: string;
}

export const FITNESS_PRESETS: RequestPreset[] = [
  {
    key: 'caps',
    title: 'Шапочки для бассейна',
    request_type: 'supply',
    to_department: 'reception',
    defaultQty: 40,
    icon: '🩲',
  },
  {
    key: 'towels-supply',
    title: 'Полотенца — нужно ещё',
    request_type: 'supply',
    to_department: 'reception',
    defaultQty: 30,
    icon: '🧺',
  },
  {
    key: 'towels-pickup',
    title: 'Забрать использованные полотенца',
    request_type: 'pickup',
    to_department: 'reception',
    icon: '↩️',
  },
  {
    key: 'repair',
    title: 'Поломка инвентаря — нужен ремонт',
    request_type: 'repair',
    to_department: 'maintenance',
    icon: '🔧',
  },
  {
    key: 'custom',
    title: 'Свой вариант',
    request_type: 'other',
    to_department: 'reception',
    icon: '✏️',
  },
];
